import { Position } from 'geojson'
import { LngLatBoundsLike } from 'mapbox-gl'
import { FC, useEffect } from 'react'
import { Colors } from 'src/components/theme'
import { EMPTY_MAP_DATA_SOURCE } from 'src/constants/mapbox'
import { useMap } from 'src/hooks/use-mapbox-map'
import { useMapboxHelper } from 'src/utils/mapbox-main-map'
import turf from 'src/utils/turf'
import US_STATES from 'src/components/states-layer/us-states.json'

const EVERYWHERE = [
  [
    [0, 90],
    [180, 90],
    [180, -90],
    [0, -90],
    [-180, -90],
    [-180, 0],
    [-180, 90],
    [0, 90],
  ],
]

const CONTINENTAL_US_BOUNDS: {
  center: [number, number]
  northEast: [number, number]
  southWest: [number, number]
} = {
  center: [-98.4079974, 40.5488317],
  northEast: [-66.9513812, 49.3457868],
  southWest: [-124.7844079, 24.7433195],
}

export const US_STATES_SOURCE_ID = 'us-states-source'
export const US_STATES_FILL_LAYER_ID = 'us-states-fill-layer'

export const US_STATE_MASK_SOURCE_ID = 'us-state-mask-source'
export const US_STATE_MASK_FILL_LAYER_ID = 'us-states-mask-fill-layer'
export const US_STATE_MASK_LINE_LAYER_ID = 'us-states-mask-line-layer'

export type StateFeatureProperties = {
  stateName: string
  stateCode: string
  totalParcels: number
  opacity: number
}

type StatesLayerProps = {
  features: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon, StateFeatureProperties>[]
  color: string
  selectedStateCode?: string
}

export const StatesLayer: FC<StatesLayerProps> = ({ selectedStateCode, features, color }) => {
  const map = useMap()
  const { useSource, useLayer, setSourceData } = useMapboxHelper(map)

  useSource(US_STATES_SOURCE_ID, {
    type: 'geojson',
    data: EMPTY_MAP_DATA_SOURCE,
  })

  useLayer({
    id: US_STATES_FILL_LAYER_ID,
    source: US_STATES_SOURCE_ID,
    type: 'fill',
    paint: {
      'fill-color': color,
      'fill-opacity': ['get', 'opacity'],
    },
  })

  useEffect(() => {
    if (features.length === 0) {
      setSourceData(US_STATES_SOURCE_ID, EMPTY_MAP_DATA_SOURCE)
      return
    }

    setSourceData(US_STATES_SOURCE_ID, {
      type: 'FeatureCollection',
      features,
    })
  }, [features])

  useSource(US_STATE_MASK_SOURCE_ID, {
    type: 'geojson',
    data: EMPTY_MAP_DATA_SOURCE,
  })

  useLayer({
    id: US_STATE_MASK_FILL_LAYER_ID,
    source: US_STATE_MASK_SOURCE_ID,
    type: 'fill',
    paint: {
      'fill-color': Colors.bgWhite,
      'fill-opacity': 0.6,
    },
  })

  useLayer({
    id: US_STATE_MASK_LINE_LAYER_ID,
    source: US_STATE_MASK_SOURCE_ID,
    type: 'line',
    paint: {
      'line-color': Colors.neutral800,
      'line-width': 1,
    },
  })

  useEffect(() => {
    const selectedStateFeature = US_STATES.features.find((feature) => feature.properties.code === selectedStateCode)

    if (selectedStateFeature) {
      map.setLayoutProperty(US_STATES_FILL_LAYER_ID, 'visibility', 'none')
      map.setLayoutProperty(US_STATE_MASK_FILL_LAYER_ID, 'visibility', 'visible')
      map.setLayoutProperty(US_STATE_MASK_LINE_LAYER_ID, 'visibility', 'visible')
      const bounds = turf.bbox(selectedStateFeature)
      map.fitBounds(bounds as LngLatBoundsLike)

      const maskFeature = turf.difference(
        turf.polygon(EVERYWHERE),
        turf.multiPolygon(selectedStateFeature.geometry.coordinates as Position[][][])
      )
      setSourceData(US_STATE_MASK_SOURCE_ID, {
        type: 'FeatureCollection',
        features: [maskFeature],
      })
    } else {
      map.setLayoutProperty(US_STATES_FILL_LAYER_ID, 'visibility', 'visible')
      map.setLayoutProperty(US_STATE_MASK_FILL_LAYER_ID, 'visibility', 'none')
      map.setLayoutProperty(US_STATE_MASK_LINE_LAYER_ID, 'visibility', 'none')
      map.fitBounds([CONTINENTAL_US_BOUNDS.northEast, CONTINENTAL_US_BOUNDS.southWest])

      setSourceData(US_STATE_MASK_SOURCE_ID, EMPTY_MAP_DATA_SOURCE)
    }
  }, [selectedStateCode])

  return null
}
